'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useI18n } from 'next-localization';

import { useBasePath } from '@/hooks/use-base-path';
import { useCustomRouting } from '@/hooks/use-custom-routing';
import { useDistanceContext } from '@/hooks/use-distance-context';
import { useProviderLocation } from '@/hooks/use-provider-location';
import { getPhysicianProfileUrl, physicianDisplayTitle } from '@/lib/utils';
import { NeuStepper } from '@hcahealthcaredpx/neutron-react';

import { ProviderLocation } from '../profile/profile.types';
import { LeaveSiteModal } from './components/leave-site-modal';
import { StepOne } from './components/step-one';
import { TimeNotBookedErrorModal } from './components/step-one/calendar/time-not-booked-modal';
import { TimeReleasedModal } from './components/step-one/calendar/time-released-modal';
import { TimeRemainingModal } from './components/step-one/calendar/time-remaining-modal';
import { TimeUnavailableModal } from './components/step-one/calendar/time-unavailable-modal';
import { SCROLL_TIMEOUT } from './components/step-one/constants';
import { StepThree } from './components/step-three';
import { StepTwo } from './components/step-two';
import { useTimeslots } from './hooks/use-timeslots';
import { BookingProps, Status } from './booking.types';
import { useSteps } from './hooks';

const Booking = ({ rendering }: BookingProps): JSX.Element => {
  const stepOneRef = useRef<{
    resetTimes: () => void;
    calendarRef: React.RefObject<HTMLDivElement>;
  }>({
    resetTimes: () => {},
    calendarRef: React.createRef<HTMLDivElement>(),
  });
  const [leaveSiteModal, setLeaveSiteModal] = useState(false);
  const {
    steps,
    isFirstStep,
    isSecondStep,
    isLastStep,
    goToNextStep,
    goToPreviousStep,
    goToFirstStep,
  } = useSteps();
  const { t } = useI18n();
  const { routerPush } = useCustomRouting();
  const { withBasePath } = useBasePath();
  const { fields } = rendering;

  const currentStepIndex = useMemo(
    () => steps.findIndex((step) => step.status === Status.Active),
    [steps]
  );

  const { providerLocations, physicianNpi } = fields.physicianData;
  const { setActiveProviderLocationByDoctorId, providerLocations: contextProviderLocations } =
    useDistanceContext();

  const getProviderActiveLocation = useProviderLocation();
  const location = useMemo(
    () => getProviderActiveLocation(contextProviderLocations || providerLocations, physicianNpi),
    [getProviderActiveLocation, contextProviderLocations, providerLocations, physicianNpi]
  );

  const setLocation = useCallback(
    (location: ProviderLocation) => {
      const activeProviderId = fields?.physicianData?.physicianNpi;
      setActiveProviderLocationByDoctorId((prev) => ({
        ...prev,
        [activeProviderId]: location.id,
      }));
    },
    [setActiveProviderLocationByDoctorId, fields?.physicianData?.physicianNpi]
  );

  const timeslots = useTimeslots();

  const handleBeforeUnload = useCallback((event: BeforeUnloadEvent) => {
    event?.preventDefault();
    if (sessionStorage.getItem('bookingAlertDismissed')) {
      return;
    }
    return true;
  }, []);

  const visitDoctorProfile = useCallback(() => {
    //  if not dismissed
    if (sessionStorage.getItem('bookingAlertDismissed')) {
      const url = getPhysicianProfileUrl(fields.physicianData.urlTitle);
      routerPush(withBasePath(url));
      return;
    }
    window.removeEventListener('beforeunload', handleBeforeUnload);
    setLeaveSiteModal(true);
  }, [fields.physicianData.urlTitle, handleBeforeUnload, withBasePath, routerPush]);

  useEffect(() => {
    if (window) {
      window.addEventListener('beforeunload', handleBeforeUnload);
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
      };
    }
    return () => {};
  }, [handleBeforeUnload]);

  const timeRemainingRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const timeReleasedRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Makes sure timeouts are cleared
  useEffect(() => {
    return () => {
      timeRemainingRef.current = null;
      timeReleasedRef.current = null;
    };
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      if (currentStepIndex === 0) {
        return;
      }

      if (currentStepIndex > 0) {
        const newStepperState = [...steps];

        newStepperState[currentStepIndex].status = Status.Pending;
        newStepperState[currentStepIndex - 1].status = Status.Active;

        window.history.replaceState(
          { stepperState: newStepperState },
          '',
          window.location.pathname
        );

        setTimeout(() => {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, SCROLL_TIMEOUT);

        goToPreviousStep();
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, [steps, currentStepIndex, goToPreviousStep]);

  //  Todo: this needs to be implemented in order to be able to switch steps trough stepper buttons
  //  This is not from the requirements / can be implemented if needed / out of score for now
  // const handleStepperClick = useCallback(
  //   (
  //     e: {
  //       // todo: check for correct type
  //       detail: {
  //         label: string;
  //       };
  //     } & NeuStepperChangeEvent
  //   ) => {
  //     const stepIndex = steps.findIndex((x) => x.label === e.detail?.label);

  //     if (stepIndex < currentStepIndex) {
  //       goToPreviousStep();
  //     }
  //   },
  //   [steps, currentStepIndex, goToPreviousStep]
  // );

  const { physicianData, physicianFullName } = fields;
  const goToDoctorProfile = () =>
    routerPush(withBasePath(getPhysicianProfileUrl(physicianData.urlTitle)));
  const refreshSearch = () => {
    goToFirstStep(() => {
      stepOneRef.current?.calendarRef.current?.scrollIntoView({ behavior: 'smooth' });
      stepOneRef.current.resetTimes();
    });
  };

  return (
    <div data-test-id={rendering?.componentName}>
      <header className="py-2">
        <div className="container">
          <div className="row py-4">
            <div className="col-sm-12 col-md-4 col-lg-6">
              <h2 className="text-color-asphalt fw-bold fs-26">
                {physicianDisplayTitle(physicianFullName, physicianData.physicianDesignation)}
              </h2>

              <span className="text-color-asphalt d-inline-block pt-2">
                {isLastStep
                  ? t('OAS.Booking.ReviewAndConfirmYourInformation')
                  : t('OAS.Booking.RequiredField')}
              </span>
            </div>
            <div className="col-sm-12 col-md-8 col-lg-6">
              <NeuStepper kind="horizontal" steps={steps} />
            </div>
          </div>
        </div>
      </header>

      <div style={{ backgroundColor: 'white', paddingBottom: '50px' }}>
        <div className="container">
          {isFirstStep && (
            <StepOne
              providerLocations={contextProviderLocations || providerLocations}
              fields={fields}
              goToNextStep={goToNextStep}
              goToPreviousStep={visitDoctorProfile}
              location={location}
              setLocation={setLocation}
              ref={stepOneRef}
              timeRemainingRef={timeRemainingRef}
              timeReleasedRef={timeReleasedRef}
              {...timeslots}
            />
          )}
          {isSecondStep && (
            <StepTwo goToNextStep={goToNextStep} goToPreviousStep={goToPreviousStep} />
          )}
          {isLastStep && (
            <StepThree
              goToPreviousStep={goToPreviousStep}
              physicianData={physicianData}
              physicianName={physicianFullName}
              location={location}
              beforeUnloadHandler={handleBeforeUnload}
              siteType={fields.siteType}
            />
          )}
        </div>
      </div>

      <TimeRemainingModal />
      <TimeReleasedModal
        skipDeclineHandlerOnDismiss
        onModalAccept={refreshSearch}
        onModalDecline={goToDoctorProfile}
      />
      <TimeUnavailableModal
        // Refresh -> It takes the user back to Appointment time section where the user can select a different timeslot
        // we are doing this with imperative handle,
        // this way step one magic stays there and doesn't rerender the whole booking stepper
        onModalAccept={refreshSearch}
        onModalDecline={goToDoctorProfile}
        skipDeclineHandlerOnDismiss
      />
      <TimeNotBookedErrorModal
        onModalAccept={refreshSearch}
        onModalDecline={goToDoctorProfile}
        skipDeclineHandlerOnDismiss
      />

      {/* Todo: implement this for navigating away from the page in a generic way. */}
      <LeaveSiteModal
        show={leaveSiteModal}
        setShow={setLeaveSiteModal}
        url={getPhysicianProfileUrl(fields.physicianData.urlTitle)}
      />
    </div>
  );
};

export default Booking;
